import { Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router'
import { Toaster } from '@/components/ui/sonner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { FullPageSpinner } from '@/components/Spinner'
import { useAuth } from '@/hooks/useAuth'
import { useLaunchQueue } from '@/hooks/useLaunchQueue'
import { useKeyboardAware } from '@/hooks/useKeyboardAware'
import { useNotificationRouting } from '@/hooks/useNotificationRouting'
import { useRole } from '@/hooks/useRole'
import { lazyRoute } from '@/lib/lazyRoute'
import { mfaGateDecision, POST_MFA_REDIRECT_KEY } from '@/lib/mfa'
import { OAUTH_CONSENT_PATH, stashConsentReturn } from '@/lib/oauthConsent'
import { isPublicIntakePath, isPublicPurchasePath, isTenantSlugPath } from '@/lib/tenantPaths'

import { OperatorLayout } from '@/routes/OperatorLayout'
import { CustomerLayout } from '@/routes/CustomerLayout'
import { TenantLayout } from '@/routes/TenantLayout'
import { RootIndex } from '@/routes/RootIndex'

// Login and the tenant chat entry stay EAGER: they are the first paint for
// signed-out and signed-in users respectively, and a spinner-then-content flash
// on the very first screen reads as a broken app.
import Login from '@/pages/Login'
import TenantChat from '@/pages/TenantChat'

// Everything else is split per route. The prize is the reporting screens —
// Overview pulls in Recharts (~415 kB raw), which no one logging in or chatting
// should have to download. Layout routes stay eager so the nav rail never blinks.
//
// lazyRoute, not lazy: a code-split screen is fetched at click time, so a deploy
// that lands while the app is open leaves the old hashed chunks unreachable and
// every not-yet-visited menu item throws. See lib/lazyRoute.ts.
const Verify = lazyRoute(() => import('@/pages/Verify'))
const MfaChallenge = lazyRoute(() => import('@/pages/MfaChallenge'))
const ResetPassword = lazyRoute(() => import('@/pages/ResetPassword'))
const Privacy = lazyRoute(() => import('@/pages/Privacy'))
const Terms = lazyRoute(() => import('@/pages/Terms'))
const Tokens = lazyRoute(() => import('@/pages/Tokens'))
const OAuthConsent = lazyRoute(() => import('@/pages/OAuthConsent'))
const PackagePurchase = lazyRoute(() => import('@/pages/PackagePurchase'))
const AuthCallback = lazyRoute(() => import('@/pages/AuthCallback'))
const CustomerChat = lazyRoute(() => import('@/pages/CustomerChat'))

const OperatorChat = lazyRoute(() => import('@/pages/operator/OperatorChat'))
const Overview = lazyRoute(() => import('@/pages/operator/Overview'))
const Schedule = lazyRoute(() => import('@/pages/operator/Schedule'))
const Inbox = lazyRoute(() => import('@/pages/operator/Inbox'))
const Conversation = lazyRoute(() => import('@/pages/operator/Conversation'))
const Customers = lazyRoute(() => import('@/pages/operator/Customers'))
const CustomerDetail = lazyRoute(() => import('@/pages/operator/CustomerDetail'))
const CustomerEditor = lazyRoute(() => import('@/pages/operator/CustomerEditor'))
const Team = lazyRoute(() => import('@/pages/operator/Team'))
const Bookings = lazyRoute(() => import('@/pages/operator/Bookings'))
const BookingDetail = lazyRoute(() => import('@/pages/operator/BookingDetail'))
const Payments = lazyRoute(() => import('@/pages/operator/Payments'))
const Refunds = lazyRoute(() => import('@/pages/operator/Refunds'))
const PackageSales = lazyRoute(() => import('@/pages/operator/PackageSales'))
const NewSale = lazyRoute(() => import('@/pages/operator/NewSale'))
const PaymentRequests = lazyRoute(() => import('@/pages/operator/PaymentRequests'))
const Memberships = lazyRoute(() => import('@/pages/operator/Memberships'))
const Services = lazyRoute(() => import('@/pages/operator/Services'))
const ServiceCategories = lazyRoute(() => import('@/pages/operator/ServiceCategories'))
const ServiceEditor = lazyRoute(() => import('@/pages/operator/ServiceEditor'))
const Packages = lazyRoute(() => import('@/pages/operator/Packages'))
const PackageEditor = lazyRoute(() => import('@/pages/operator/PackageEditor'))
const Resources = lazyRoute(() => import('@/pages/operator/Resources'))
const ResourceEditor = lazyRoute(() => import('@/pages/operator/ResourceEditor'))
const Hours = lazyRoute(() => import('@/pages/operator/Hours'))
const Schedules = lazyRoute(() => import('@/pages/operator/Schedules'))
const TimeBlocks = lazyRoute(() => import('@/pages/operator/TimeBlocks'))
const CalendarNotes = lazyRoute(() => import('@/pages/operator/CalendarNotes'))
const IntakeForms = lazyRoute(() => import('@/pages/operator/IntakeForms'))
const IntakeFormEditor = lazyRoute(() => import('@/pages/operator/IntakeFormEditor'))
const IntakeFormPreview = lazyRoute(() => import('@/pages/operator/IntakeFormPreview'))
const TreatmentCards = lazyRoute(() => import('@/pages/operator/TreatmentCards'))
const TreatmentCardEditor = lazyRoute(() => import('@/pages/operator/TreatmentCardEditor'))
const IntakeSubmissions = lazyRoute(() => import('@/pages/operator/IntakeSubmissions'))
const IntakeSubmissionDetail = lazyRoute(() => import('@/pages/operator/IntakeSubmissionDetail'))
// Public — a customer with the link and NO session fills their form in here.
const PublicIntake = lazyRoute(() => import('@/pages/PublicIntake'))
const OpenSlots = lazyRoute(() => import('@/pages/operator/OpenSlots'))
const Templates = lazyRoute(() => import('@/pages/operator/Templates'))
const TemplateEditor = lazyRoute(() => import('@/pages/operator/TemplateEditor'))
const ReminderLog = lazyRoute(() => import('@/pages/operator/ReminderLog'))
const Notifications = lazyRoute(() => import('@/pages/operator/Notifications'))
const NotificationEditor = lazyRoute(() => import('@/pages/operator/NotificationEditor'))
const NotificationLog = lazyRoute(() => import('@/pages/operator/NotificationLog'))
const Sequences = lazyRoute(() => import('@/pages/operator/Sequences'))
const SequenceDetail = lazyRoute(() => import('@/pages/operator/SequenceDetail'))
const SequenceLog = lazyRoute(() => import('@/pages/operator/SequenceLog'))
const Campaigns = lazyRoute(() => import('@/pages/operator/Campaigns'))
const CampaignDetail = lazyRoute(() => import('@/pages/operator/CampaignDetail'))
const Segments = lazyRoute(() => import('@/pages/operator/Segments'))
const SegmentDetail = lazyRoute(() => import('@/pages/operator/SegmentDetail'))
const Settings = lazyRoute(() => import('@/pages/operator/Settings'))
const Setup = lazyRoute(() => import('@/pages/operator/Setup'))
const Provisioning = lazyRoute(() => import('@/pages/operator/Provisioning'))

/** Reachable signed-out. Mirrors the old root layout's public-path allowlist. */
const PUBLIC_PATHS = ['/privacy', '/terms', '/tokens', '/login', '/verify', '/auth/callback']

function AuthGate({ children }: { children: React.ReactNode }) {
  const { authState } = useAuth()
  const { status: roleStatus, userRole } = useRole()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const mfaRequired = authState.status === 'authenticated' && authState.mfaRequired
  // Staff/owner only — customers are never challenged, so a factor-bearing
  // session waits for role resolution before either rendering or redirecting.
  const mfaDecision = mfaGateDecision(mfaRequired, roleStatus, userRole)

  useEffect(() => {
    if (authState.status === 'loading') return
    if (PUBLIC_PATHS.includes(pathname)) return

    if (authState.status === 'unauthenticated') {
      // A magic-link redirect can land on any path with the token still in the
      // URL. Hand off to the callback handler first or the token is lost.
      const params = new URLSearchParams(window.location.search)
      const hash = window.location.hash
      if (params.get('code') || params.get('token_hash') || hash.includes('access_token=')) {
        navigate('/auth/callback', { replace: true })
        return
      }
      // Tenant slug pages stay reachable signed-out: guests book anonymously
      // through the widget flow, and the page itself offers the sign-in path.
      if (isTenantSlugPath(pathname)) return
      // The public intake filler too — the customer holding the link has no
      // account; the token in the URL is the credential.
      if (isPublicIntakePath(pathname)) return
      // And the public package shop: a purchase link is shared with people who
      // have never used this business, so bouncing them to /login loses the sale.
      if (isPublicPurchasePath(pathname)) return
      stashConsentReturn(pathname, search)
      navigate('/login', { replace: true })
      return
    }

    // An authenticator is enrolled but this session hasn't passed the code yet
    // (aal1) — nothing renders until it does. Remember where they were headed.
    if (mfaDecision === 'challenge' && pathname !== '/mfa') {
      // The consent page is the one destination whose query string is the whole point.
      sessionStorage.setItem(
        POST_MFA_REDIRECT_KEY,
        pathname === OAUTH_CONSENT_PATH ? pathname + search : pathname,
      )
      navigate('/mfa', { replace: true })
    }
  }, [authState.status, mfaDecision, pathname, search, navigate])

  if (authState.status === 'loading' && !isPublicIntakePath(pathname)) return <FullPageSpinner />
  // Don't paint protected content behind the redirect to the MFA screen (or
  // while the staff-vs-customer decision is still resolving). The public
  // intake filler is exempt — it renders no protected content at all.
  if (
    mfaDecision !== 'allow' && pathname !== '/mfa'
    && !PUBLIC_PATHS.includes(pathname) && !isPublicIntakePath(pathname)
  ) return <FullPageSpinner />
  return <>{children}</>
}

export function App() {
  // No favicon effect here. The default icon is declared in index.html, and the
  // tenant's own logo replaces it once branding resolves (lib/documentBranding).
  // This used to append a second <link rel="icon"> on mount, which left two icon
  // links in the document for the browser to choose between — and its cleanup
  // would throw once the branding code removed that link first.
  const { pathname } = useLocation()

  // Mounted ONCE, here, so every screen, dialog and the assistant panel share one
  // measurement of the keyboard. Per-screen would mean several listeners fighting
  // over the same CSS variable.
  useKeyboardAware()
  useNotificationRouting()
  useLaunchQueue()

  return (
    <AuthGate>
      {/* Inner boundary, in ADDITION to the app-level one in main.tsx. That one
          sits outside the router so it survives a crash in a layout, which means
          it cannot see navigation and stays on the error screen for ever. This one
          can: it clears when the route changes, so a screen that fails does not
          take every subsequent menu click with it. */}
      <ErrorBoundary source="screen" resetKey={pathname}>
      {/* One Suspense boundary around the whole table rather than per route:
          a route swap should show one spinner, not tear down the nav shell. */}
      <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route path="/" element={<RootIndex />} />

        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/verify" element={<Verify />} />
        {/* Session-required auth steps — deliberately NOT in PUBLIC_PATHS. */}
        <Route path="/mfa" element={<MfaChallenge />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* OAuth consent for the hj-porter MCP connector: needs a full (MFA-passed) session. */}
        <Route path={OAUTH_CONSENT_PATH} element={<OAuthConsent />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {/* Public by design: the customer filling in their intake form has no
            account — the unguessable token is the credential (AuthGate exempts
            the path via isPublicIntakePath). */}
        <Route path="/intake/:token" element={<PublicIntake />} />
        <Route path="/tokens" element={<Tokens />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Operator, but drawn as the client's page rather than inside the shell.
            Protected like every operator route: the AuthGate, then the operator read. */}
        <Route path="/intake-form/:id/preview" element={<IntakeFormPreview />} />

        {/* Operator. These sat under an Expo Router (operator) group, which never
            appeared in the URL — so every path here is unchanged from before. */}
        <Route element={<OperatorLayout />}>
          <Route path="/chat" element={<OperatorChat />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/provisioning" element={<Provisioning />} />
          <Route path="/overview" element={<Overview />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/conversation/:id" element={<Conversation />} />
          <Route path="/customers" element={<Customers />} />
          {/* Static segments outrank dynamic ones in React Router, so /customer/new
              can never be swallowed by /customer/:id regardless of order. */}
          <Route path="/customer/new" element={<CustomerEditor />} />
          <Route path="/customer/:id" element={<CustomerDetail />} />
          <Route path="/customer/:id/edit" element={<CustomerEditor />} />
          <Route path="/team" element={<Team />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/booking/:id" element={<BookingDetail />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/new-sale" element={<NewSale />} />
          <Route path="/package-sales" element={<PackageSales />} />
          <Route path="/payment-requests" element={<PaymentRequests />} />
          <Route path="/memberships" element={<Memberships />} />
          <Route path="/services" element={<Services />} />
          <Route path="/service-categories" element={<ServiceCategories />} />
          {/* ':id' is 'new' when creating — the editors branch on it, the same way
              /segment/new and /sequence/new already do. */}
          <Route path="/service/:id" element={<ServiceEditor />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/package/:id" element={<PackageEditor />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resource/:id" element={<ResourceEditor />} />
          <Route path="/hours" element={<Hours />} />
          <Route path="/schedules" element={<Schedules />} />
          <Route path="/time-blocks" element={<TimeBlocks />} />
          <Route path="/calendar-notes" element={<CalendarNotes />} />
          <Route path="/open-slots" element={<OpenSlots />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/template/:id" element={<TemplateEditor />} />
          <Route path="/reminder-log" element={<ReminderLog />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notification/:id" element={<NotificationEditor />} />
          <Route path="/notification-log" element={<NotificationLog />} />
          <Route path="/sequences" element={<Sequences />} />
          <Route path="/sequence/:id" element={<SequenceDetail />} />
          <Route path="/sequence-log" element={<SequenceLog />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaign/:id" element={<CampaignDetail />} />
          {/* Email templates were folded into campaigns (2026-09-09) — a
              campaign owns its email and it is edited in the wizard. Both paths
              survive as redirects so bookmarks and the PWA's cached shell keep
              working, the same treatment /chat got when it became a panel. */}
          <Route path="/email-templates" element={<Navigate to="/campaigns" replace />} />
          <Route path="/email-template/:id" element={<Navigate to="/campaigns" replace />} />
          <Route path="/segments" element={<Segments />} />
          <Route path="/segment/:id" element={<SegmentDetail />} />
          <Route path="/intake-forms" element={<IntakeForms />} />
          <Route path="/intake-form/:id" element={<IntakeFormEditor />} />
          <Route path="/treatment-cards" element={<TreatmentCards />} />
          <Route path="/treatment-card/:id" element={<TreatmentCardEditor />} />
          <Route path="/intake-submissions" element={<IntakeSubmissions />} />
          <Route path="/intake-submission/:id" element={<IntakeSubmissionDetail />} />
          <Route path="/settings" element={<Settings />} />
          {/* One page per settings topic; the Settings menu lists them. */}
          <Route path="/settings/:section" element={<Settings />} />
        </Route>

        {/* Customer portal */}
        <Route element={<CustomerLayout />}>
          <Route path="/portal" element={<CustomerChat />} />
        </Route>

        {/* Slug-routed tenant entry. Declared last for readability — React Router
            ranks static segments above dynamic ones regardless of order, so
            /overview can never be swallowed by /:tenant. Reachable signed-out
            (guest booking) — any NEW static top-level route above must also be
            added to RESERVED_TOP_SEGMENTS in lib/tenantPaths.ts. */}
        <Route path="/:tenant" element={<TenantLayout />}>
          <Route index element={<TenantChat />} />
          {/* The public package shop. Shareable by design — these URLs get
              texted after a consult and printed as QR codes, so they must work
              with no account. A NEW child route here is NOT reachable signed-out
              until it is named in lib/tenantPaths.ts (isPublicPurchasePath). */}
          <Route path="/:tenant/buy" element={<PackagePurchase />} />
          <Route path="/:tenant/buy/:packageId" element={<PackagePurchase />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      <Toaster />
    </AuthGate>
  )
}
